import type { Cell } from '../model/types';

const epsilon = 1e-9;

function appendPoint(points: Cell[], point: Cell): void {
  const previous = points.at(-1);
  if (previous === undefined
    || Math.hypot(point.x - previous.x, point.y - previous.y) >= epsilon) {
    points.push(point);
  }
}

/**
 * Rounds the 90-degree corners of an orthogonal grid polyline.
 *
 * This API is intentionally limited to axis-aligned segments and right-angle
 * turns. Straight vertices are preserved, while the first and last points are
 * always returned unchanged. Each fillet is flattened into
 * `segmentsPerCorner` line segments and its radius is clamped to half of each
 * neighbouring segment so consecutive corners cannot overlap.
 */
export function roundPolyline(
  points: readonly Cell[],
  radius: number,
  segmentsPerCorner: number,
): Cell[] {
  const first = points[0];
  if (first === undefined) {
    return [];
  }
  if (points.length < 3 || radius <= 0 || segmentsPerCorner < 1) {
    return [...points];
  }

  const result: Cell[] = [first];
  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const corner = points[index];
    const next = points[index + 1];
    if (previous === undefined || corner === undefined || next === undefined) {
      continue;
    }

    const incomingDelta = { x: corner.x - previous.x, y: corner.y - previous.y };
    const outgoingDelta = { x: next.x - corner.x, y: next.y - corner.y };
    const incomingLength = Math.hypot(incomingDelta.x, incomingDelta.y);
    const outgoingLength = Math.hypot(outgoingDelta.x, outgoingDelta.y);
    if (incomingLength < epsilon || outgoingLength < epsilon) {
      appendPoint(result, corner);
      continue;
    }
    const incoming = {
      x: incomingDelta.x / incomingLength,
      y: incomingDelta.y / incomingLength,
    };
    const outgoing = {
      x: outgoingDelta.x / outgoingLength,
      y: outgoingDelta.y / outgoingLength,
    };
    const cross = incoming.x * outgoing.y - incoming.y * outgoing.x;
    const dot = incoming.x * outgoing.x + incoming.y * outgoing.y;
    if (Math.abs(cross) < epsilon || Math.abs(dot) >= epsilon) {
      appendPoint(result, corner);
      continue;
    }

    const tangent = Math.min(radius, incomingLength / 2, outgoingLength / 2);
    const start = {
      x: corner.x - incoming.x * tangent,
      y: corner.y - incoming.y * tangent,
    };
    const center = {
      x: start.x - incoming.y * tangent * Math.sign(cross),
      y: start.y + incoming.x * tangent * Math.sign(cross),
    };
    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const sweep = Math.sign(cross) * Math.PI / 2;
    appendPoint(result, start);
    for (let step = 1; step <= segmentsPerCorner; step += 1) {
      const angle = startAngle + sweep * step / segmentsPerCorner;
      appendPoint(result, {
        x: center.x + Math.cos(angle) * tangent,
        y: center.y + Math.sin(angle) * tangent,
      });
    }
  }

  const last = points.at(-1);
  if (last !== undefined) {
    appendPoint(result, last);
  }
  return result;
}
